import * as React from "react";
import { ListItemButton, ListItemIcon, ListItemText, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import colorConfigs from "src/configs/colorConfigs";
import { RootState } from "src/redux/store";
import { RouteType } from "src/routes/config";
import sizeConfigs from "src/configs/sizeConfigs";
import { useTranslation } from "react-i18next";

type Props = {
  item: RouteType;
  isChild?: boolean;
};

/**
 * @function SidebarItem
 * @description Renderiza un elemento simple del menú.
 * @param {RouteType} item: Elemento menu a renderizar.
 * @returns {React.JSX.Element | null}
 */
const SidebarItem = ({ item, isChild = false }: Props): React.JSX.Element | null => {
  const { appState } = useSelector((state: RootState) => state.appState);

  const { t } = useTranslation<["Menu"]>(["Menu"]);

  if (item.disabled && item.sidebarProps) {
    return (
      <ListItemButton
        disabled
        sx={{
          paddingY: isChild ? "0.4rem" : sizeConfigs.sidebar.item.marginY,
          paddingX: isChild ? "2.5rem" : sizeConfigs.sidebar.item.marginX,
          opacity: 0.4,
          cursor: "not-allowed",
          pointerEvents: "all !important",
        }}
      >
        <ListItemIcon sx={{ minWidth: isChild ? "32px" : undefined, "& svg": { fontSize: isChild ? "1.1rem" : undefined } }}>
          {item.sidebarProps.icon}
        </ListItemIcon>
        <ListItemText
          disableTypography
          primary={
            <Typography fontWeight={isChild ? 500 : 700} fontSize={isChild ? "0.82rem" : undefined}>
              {t(item.sidebarProps.displayText)}
            </Typography>
          }
        />
      </ListItemButton>
    );
  }

  return (
    item.sidebarProps && item.path ? (
      <ListItemButton
        component={Link}
        to={item.path}
        sx={{
          "&: hover": {
            backgroundColor: colorConfigs.sidebar.hover.bg,
            color: colorConfigs.sidebar.hover.color,
          },
          backgroundColor: appState === item.state ? colorConfigs.sidebar.active.bg : colorConfigs.sidebar.bg,
          color: appState === item.state ? colorConfigs.sidebar.active.color : colorConfigs.sidebar.color,
          paddingY: isChild ? "0.4rem" : sizeConfigs.sidebar.item.marginY,
          paddingX: isChild ? "2.5rem" : sizeConfigs.sidebar.item.marginX,
        }}
      >
        <ListItemIcon
            sx={{
              color: appState === item.state ? colorConfigs.sidebar.active.color : colorConfigs.sidebar.color,
              minWidth: isChild ? "32px" : undefined,
              "& svg": { fontSize: isChild ? "1.1rem" : undefined },
            }}
        >
          {item.sidebarProps.icon && item.sidebarProps.icon}
        </ListItemIcon>
        <ListItemText
          disableTypography
          primary={
            <Typography fontWeight={isChild ? 500 : 700} fontSize={isChild ? "0.82rem" : undefined}>
              {t(item.sidebarProps.displayText)}
            </Typography>
          }
        />
      </ListItemButton>
    ) : null
  );
};

export default SidebarItem;
